import React, { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./components/ui/card";
import { Button } from "./components/ui/button";
import { Badge } from "./components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./components/ui/table";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "./components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./components/ui/dialog";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { Edit, Trash2, CheckCircle, Clock, XCircle, Users, Calendar, Download, User, Settings, Shield, ChevronUp, ChevronDown, Receipt } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

// Users Tab Content
export const UsersTabContent = ({ 
  users, 
  currentUser,
  editUser, 
  deleteUser, 
  promoteUser,
  onUpdateRoles,
  onViewPaymentHistory,
  viewMode = "cards", 
  onViewModeChange 
}) => {
  const [sortConfig, setSortConfig] = useState({ 
    key: 'created_at', 
    direction: 'desc' // Latest registrations first
  });
  const [selectedYear, setSelectedYear] = useState('all'); // Filter by membership year
  const [editingUser, setEditingUser] = useState(null);
  const [rolesEditUser, setRolesEditUser] = useState(null);
  const [selectedRoles, setSelectedRoles] = useState([]);

  const ADMIN_ROLES = [
    { id: 'super_admin', name: 'Super Admin', desc: 'Full access to everything' },
    { id: 'event_manager', name: 'Event Manager', desc: 'Events, bookings, financials' },
    { id: 'membership_manager', name: 'Membership Manager', desc: 'Users, loans, media' },
    { id: 'treasurer', name: 'Treasurer', desc: 'Financials & reports (read-only events)' },
  ];

  const isSuperAdmin = currentUser?.admin_roles?.includes('super_admin') || 
    (currentUser?.role === 'Administrator' && (!currentUser?.admin_roles || currentUser.admin_roles.length === 0));
  const [userForm, setUserForm] = useState({
    first_name_1: '',
    first_name_2: '',
    last_name: '',
    email: '',
    mobile: '',
    landline: '',
    member_category: 'JRC Internal'
  });
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    type: '',
    user: null,
    onConfirm: null
  });

  // Every year a member held (falls back to their current year).
  const yearsOf = (u) => (u.membership_years?.length ? u.membership_years : [u.membership_year].filter(Boolean));
  // Payment date for the picked year (or the member's own year when "All Years").
  const paidYearFor = (user) => (selectedYear === 'all' ? user.membership_year : selectedYear);
  const paidDateFor = (user) => {
    const iso = user.membership_paid_dates?.[String(paidYearFor(user))];
    if (!iso) return null;
    const d = new Date(iso);
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
  };

  // Get unique membership years from users
  const membershipYears = useMemo(() => {
    const years = [...new Set(users.flatMap(yearsOf))];
    return years.sort((a, b) => b - a); // Descending order
  }, [users]);

  // Filter users by selected year
  const filteredUsers = useMemo(() => {
    if (selectedYear === 'all') {
      return users;
    }
    return users.filter(u => yearsOf(u).includes(parseInt(selectedYear)));
  }, [users, selectedYear]);

  const formatDate = (dateStr) => {
    try {
      const [day, month, year] = dateStr.split('/');
      return `${day}/${month}/${year}`;
    } catch {
      return dateStr;
    }
  };

  const formatRegistrationDate = (createdAtStr) => {
    try {
      const date = new Date(createdAtStr);
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit', 
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
    } catch {
      return 'N/A';
    }
  };

  const getUserStatus = (user) => {
    if (user.is_verified) {
      return <Badge variant="default" className="bg-green-100 text-green-800">Verified</Badge>;
    } else {
      return <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">Pending</Badge>;
    }
  };

  // Sorting function
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Sort users based on current sort configuration
  const sortedUsers = useMemo(() => {
    const sortableUsers = [...filteredUsers];
    if (sortConfig.key) {
      sortableUsers.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];
        
        // Handle date sorting for created_at (registration date)
        if (sortConfig.key === 'created_at') {
          aValue = new Date(aValue);
          bValue = new Date(bValue);
        }
        
        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableUsers;
  }, [filteredUsers, sortConfig]);

  const SortIcon = ({ column }) => {
    if (sortConfig.key !== column) {
      return null;
    }
    return sortConfig.direction === 'asc' ? 
      <ChevronUp className="h-4 w-4 inline ml-1" /> : 
      <ChevronDown className="h-4 w-4 inline ml-1" />;
  };

  // Handle user edit
  const handleEditUser = (user) => {
    setEditingUser(user);
    setUserForm({
      first_name_1: user.first_name_1 || '',
      first_name_2: user.first_name_2 || '',
      last_name: user.last_name || '',
      email: user.email || '',
      mobile: user.mobile || '',
      landline: user.landline || '',
      member_category: user.member_category || 'JRC Internal',
      birth_date: user.birth_date || '',
      birth_place: user.birth_place || '',
      nationality: user.nationality || '',
      other_nationality: user.other_nationality || '',
      address: user.address || '',
      postal_code: user.postal_code || '',
      city: user.city || '',
      province: user.province || '',
      fiscal_code: user.fiscal_code || ''
    });
    setIsEditDialogOpen(true);
  };

  // Handle user form submission
  const handleUserSubmit = async (e) => {
    e.preventDefault();
    try {
      await editUser(editingUser, userForm);
      setIsEditDialogOpen(false);
      setEditingUser(null);
    } catch (error) {
      console.error('Failed to update user:', error);
    }
  };

  // Handle delete confirmation
  const handleDeleteConfirm = (user) => {
    setConfirmDialog({
      isOpen: true,
      type: 'delete',
      user: user,
      onConfirm: () => {
        deleteUser(user.id);
        setConfirmDialog({ isOpen: false, type: '', user: null, onConfirm: null });
      }
    });
  };

  // Handle promote confirmation
  const handlePromoteConfirm = (user) => {
    setConfirmDialog({
      isOpen: true,
      type: 'promote',
      user: user,
      onConfirm: () => {
        promoteUser(user.id, 'Administrator');
        setConfirmDialog({ isOpen: false, type: '', user: null, onConfirm: null });
      }
    });
  };

  // Export users to Excel (CSV format)
  const exportToExcel = () => {
    try {
      // Prepare CSV headers
      const headers = [
        'Member No.',
        'Linked Partner',
        'Registration Date',
        'First Name',
        'Second Name',
        'Last Name',
        'Email',
        'Mobile',
        'Landline',
        'Role',
        'Member Category',
        'Birth Date',
        'Birth Place',
        'Nationality',
        'Other Nationality',
        'Address',
        'Postal Code',
        'City',
        'Province',
        'Country',
        'Fiscal Code',
        'Membership Year',
        'Membership Status',
        'Membership Expires',
        selectedYear === 'all' ? 'Membership Paid On' : `Membership ${selectedYear} Paid On`,
        'Verified Status'
      ];

      // Prepare CSV rows
      const rows = sortedUsers.map(user => [
        user.member_number || '',
        user.partner_info ? `${user.partner_info.first_name} ${user.partner_info.last_name} (${user.partner_info.email})` : '',
        formatRegistrationDate(user.created_at),
        user.first_name_1 || '',
        user.first_name_2 || '',
        user.last_name || '',
        user.email || '',
        user.mobile || '',
        user.landline || '',
        user.role || '',
        user.member_category || '',
        user.birth_date || '',
        user.birth_place || '',
        user.nationality || '',
        user.other_nationality || '',
        user.address || '',
        user.postal_code || '',
        user.city || '',
        user.province || '',
        user.country || '',
        user.fiscal_code || '',
        user.membership_year || '',
        user.membership_status || '',
        user.membership_expires ? (() => { const d = new Date(user.membership_expires); return `${String(d.getUTCDate()).padStart(2,'0')}/${String(d.getUTCMonth()+1).padStart(2,'0')}/${d.getUTCFullYear()}`; })() : '',
        paidDateFor(user) || '',
        user.is_verified ? 'Verified' : 'Pending'
      ]);

      // Create CSV content
      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => {
          // Escape cells containing commas or quotes
          const cellStr = String(cell);
          if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
            return `"${cellStr.replace(/"/g, '""')}"`;
          }
          return cellStr;
        }).join(','))
      ].join('\n');

      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      // Generate filename with year filter info
      const yearInfo = selectedYear === 'all' ? 'all_years' : `year_${selectedYear}`;
      const filename = `ACE_Members_${yearInfo}_${new Date().toISOString().split('T')[0]}.csv`;
      
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      console.log(`Exported ${sortedUsers.length} users to ${filename}`);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      alert('Failed to export users. Please try again.');
    }
  };


  // Card View
  if (viewMode === "cards") {
    return (
      <div className="space-y-6">
        {/* Filter Bar */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-lg border border-blue-200">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex items-center gap-3">
              <Users className="h-5 w-5 text-blue-600" />
              <div>
                <h3 className="font-semibold text-gray-900">User Management</h3>
                <p className="text-sm text-gray-600">
                  {selectedYear === 'all' 
                    ? `Showing all ${sortedUsers.length} users` 
                    : `Showing ${sortedUsers.length} users for ${selectedYear}`}
                </p>
              </div>
            </div>
            
            {/* Year Filter and Export Buttons */}
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700">Membership Year:</label>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Years ({users.length})</option>
                  {membershipYears.map(year => {
                    const count = users.filter(u => yearsOf(u).includes(year)).length;
                    return (
                      <option key={year} value={year}>
                        {year} ({count} members)
                      </option>
                    );
                  })}
                </select>
              </div>
              
              {/* Export Button */}
              <Button
                onClick={exportToExcel}
                className="bg-emerald-600 hover:bg-emerald-700 text-white flex items-center gap-2"
                size="sm"
              >
                <Download className="h-4 w-4" />
                Export to Excel
              </Button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {sortedUsers.map((user) => (
            <Card key={user.id} className="hover:shadow-lg transition-shadow border-l-4 border-l-blue-500">
              <CardHeader className="pb-3">
                <div className="flex justify-between items-start">
                  <div className="flex items-center gap-2">
                    <Users className="h-5 w-5 text-slate-600" />
                    <CardTitle className="text-lg">
                      {user.first_name_1} {user.first_name_2 ? user.first_name_2 + ' ' : ''}{user.last_name}
                    </CardTitle>
                  </div>
                  <div className="flex gap-1">
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      onClick={() => handleEditUser(user)}
                      title="Edit user"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    {onViewPaymentHistory && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => onViewPaymentHistory(user)}
                        title="Payment history"
                        data-testid={`payment-history-btn-${user.id}`}
                      >
                        <Receipt className="h-4 w-4 text-emerald-700" />
                      </Button>
                    )}
                    <Button 
                      size="sm" 
                      variant="ghost"
                      onClick={() => handleDeleteConfirm(user)}
                      title="Delete user"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                    {isSuperAdmin && user.id !== currentUser?.id && onUpdateRoles && (
                      <Button 
                        size="sm" 
                        variant="ghost"
                        onClick={() => { setRolesEditUser(user); setSelectedRoles(user.admin_roles || []); }}
                        title="Manage admin roles"
                      >
                        <Shield className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 text-sm">
                  <div>
                    <span className="font-medium text-slate-700">Registration Date:</span>
                    <p className="text-slate-600 flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {formatRegistrationDate(user.created_at)}
                    </p>
                  </div>

                  <div>
                    <span className="font-medium text-slate-700">Membership {paidYearFor(user) || ''} paid on:</span>
                    <p className="text-slate-600" data-testid={`paid-date-card-${user.id}`}>{paidDateFor(user) || '—'}</p>
                  </div>

                  <div>
                    <span className="font-medium text-slate-700">Email:</span>
                    <p className="text-slate-600">{user.email}</p>
                  </div>
                  
                  <div className="flex flex-wrap gap-2">
                    <Badge variant={user.role === 'Administrator' ? "default" : "secondary"}>
                      {user.role || 'Regular User'}
                    </Badge>
                    <Badge variant="outline">{user.member_category}</Badge>
                    {getUserStatus(user)}
                  </div>

                  <div>
                    <span className="font-medium text-slate-700">Location:</span>
                    <p className="text-slate-600">
                      {user.city && user.province ? `${user.city}, ${user.province}` : 'N/A'}
                    </p>
                  </div>

                  <div>
                    <span className="font-medium text-slate-700">Nationality:</span>
                    <p className="text-slate-600">
                      {user.nationality || 'N/A'}
                      {user.other_nationality && ` (${user.other_nationality})`}
                    </p>
                  </div>

                  <div>
                    <span className="font-medium text-slate-700">Birth:</span>
                    <p className="text-slate-600">{formatDate(user.birth_date)} - {user.birth_place}</p>
                  </div>

                  {user.partner_info && (
                    <div>
                      <span className="font-medium text-purple-700">Linked Partner:</span>
                      <p className="text-slate-600">{user.partner_info.first_name} {user.partner_info.last_name}</p>
                      <p className="text-slate-500 text-xs">{user.partner_info.email}</p>
                    </div>
                  )}

                  <div>
                    <span className="font-medium text-slate-700">Address:</span>
                    <p className="text-slate-600">
                      {user.address}<br />
                      {user.postal_code} {user.city} ({user.province})
                    </p>
                  </div>

                  <div>
                    <span className="font-medium text-slate-700">Fiscal Code:</span>
                    <p className="text-slate-600 font-mono">{user.fiscal_code}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Confirmation Dialogs */}
        <AlertDialog open={confirmDialog.isOpen} onOpenChange={(open) => !open && setConfirmDialog({ isOpen: false, type: '', user: null, onConfirm: null })}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {confirmDialog.type === 'delete' ? 'Delete User' : 'Promote to Administrator'}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {confirmDialog.type === 'delete' 
                  ? `Are you sure you want to delete "${confirmDialog.user?.first_name_1} ${confirmDialog.user?.first_name_2 ? confirmDialog.user?.first_name_2 + ' ' : ''}${confirmDialog.user?.last_name}"? This action cannot be undone and will remove all their data from the system.`
                  : `Are you sure you want to promote "${confirmDialog.user?.first_name_1} ${confirmDialog.user?.first_name_2 ? confirmDialog.user?.first_name_2 + ' ' : ''}${confirmDialog.user?.last_name}" to Administrator? They will have full access to all system features.`
                }
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction 
                onClick={confirmDialog.onConfirm}
                className={confirmDialog.type === 'delete' ? "bg-red-600 hover:bg-red-700" : "bg-blue-600 hover:bg-blue-700"}
              >
                {confirmDialog.type === 'delete' ? 'Delete User' : 'Promote to Admin'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Edit User Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Edit className="h-5 w-5" />
                Edit User: {editingUser?.first_name_1} {editingUser?.first_name_2 ? editingUser?.first_name_2 + ' ' : ''}{editingUser?.last_name}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleUserSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="first_name_1">First Name 1 *</Label>
                  <Input
                    id="first_name_1"
                    value={userForm.first_name_1}
                    onChange={(e) => setUserForm({...userForm, first_name_1: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="first_name_2">First Name 2</Label>
                  <Input
                    id="first_name_2"
                    value={userForm.first_name_2}
                    onChange={(e) => setUserForm({...userForm, first_name_2: e.target.value})}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="mobile">Mobile *</Label>
                  <Input
                    id="mobile"
                    type="tel"
                    value={userForm.mobile}
                    onChange={(e) => setUserForm({...userForm, mobile: e.target.value})}
                    placeholder="+39 xxx xxx xxxx"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="landline">Landline</Label>
                  <Input
                    id="landline"
                    type="tel"
                    value={userForm.landline}
                    onChange={(e) => setUserForm({...userForm, landline: e.target.value})}
                    placeholder="E.g. 0332 XXXX"
                  />
                </div>
                <div>
                  <Label htmlFor="last_name">Last Name *</Label>
                  <Input
                    id="last_name"
                    value={userForm.last_name}
                    onChange={(e) => setUserForm({...userForm, last_name: e.target.value})}
                    required
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={userForm.email}
                  onChange={(e) => setUserForm({...userForm, email: e.target.value})}
                  required
                />
              </div>

              <div>
                <Label htmlFor="member_category">Member Category *</Label>
                <Select value={userForm.member_category} onValueChange={(value) => setUserForm({...userForm, member_category: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="JRC Internal">JRC Internal</SelectItem>
                    <SelectItem value="JRC External">JRC External</SelectItem>
                    <SelectItem value="Associated Member">Associated Member</SelectItem>
                    <SelectItem value="Standard">Standard</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="birth_date">Birth Date</Label>
                  <Input
                    id="birth_date"
                    value={userForm.birth_date}
                    onChange={(e) => setUserForm({...userForm, birth_date: e.target.value})}
                    placeholder="DD/MM/YYYY"
                  />
                </div>
                <div>
                  <Label htmlFor="birth_place">Birth Place</Label>
                  <Input
                    id="birth_place"
                    value={userForm.birth_place}
                    onChange={(e) => setUserForm({...userForm, birth_place: e.target.value})}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="nationality">Nationality</Label>
                  <Input
                    id="nationality"
                    value={userForm.nationality}
                    onChange={(e) => setUserForm({...userForm, nationality: e.target.value})}
                  />
                </div>
                <div>
                  <Label htmlFor="other_nationality">Other Nationality</Label>
                  <Input
                    id="other_nationality"
                    value={userForm.other_nationality}
                    onChange={(e) => setUserForm({...userForm, other_nationality: e.target.value})}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="address">Address</Label>
                <Input
                  id="address"
                  value={userForm.address}
                  onChange={(e) => setUserForm({...userForm, address: e.target.value})}
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="postal_code">Postal Code</Label>
                  <Input
                    id="postal_code"
                    value={userForm.postal_code}
                    onChange={(e) => setUserForm({...userForm, postal_code: e.target.value})}
                  />
                </div>
                <div>
                  <Label htmlFor="city">City</Label>
                  <Input
                    id="city"
                    value={userForm.city}
                    onChange={(e) => setUserForm({...userForm, city: e.target.value})}
                  />
                </div>
                <div>
                  <Label htmlFor="province">Province</Label>
                  <Input
                    id="province"
                    value={userForm.province}
                    onChange={(e) => setUserForm({...userForm, province: e.target.value})}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="fiscal_code">Fiscal Code</Label>
                <Input
                  id="fiscal_code"
                  value={userForm.fiscal_code}
                  onChange={(e) => setUserForm({...userForm, fiscal_code: e.target.value})}
                />
              </div>

              <div className="flex justify-between pt-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setIsEditDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button type="submit">
                  Update User
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Table View - with proper column order and sorting
  return (
    <div className="space-y-6">
      {/* Filter Bar */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-lg border border-blue-200">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="flex items-center gap-3">
            <Users className="h-5 w-5 text-blue-600" />
            <div>
              <h3 className="font-semibold text-gray-900">User Management</h3>
              <p className="text-sm text-gray-600">
                {selectedYear === 'all' 
                  ? `Showing all ${sortedUsers.length} users` 
                  : `Showing ${sortedUsers.length} users for ${selectedYear}`}
              </p>
            </div>
          </div>
          
          {/* Year Filter and Export Buttons */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">Membership Year:</label>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Years ({users.length})</option>
                {membershipYears.map(year => {
                  const count = users.filter(u => yearsOf(u).includes(year)).length;
                  return (
                    <option key={year} value={year}>
                      {year} ({count} members)
                    </option>
                  );
                })}
              </select>
            </div>
            
            {/* Export Button */}
            <Button
              onClick={exportToExcel}
              className="bg-emerald-600 hover:bg-emerald-700 text-white flex items-center gap-2"
              size="sm"
            >
              <Download className="h-4 w-4" />
              Export to Excel
            </Button>
          </div>
        </div>
      </div>
      
      <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50 border-b-2 border-slate-200">
              {/* Column order (per user request):
                  Member No / Registration Date / First Name / Last Name / Email /
                  Role / Roles / Category / Linked Partner / Nationality /
                  Membership / Expires / Location / Actions */}
              <TableHead
                className="sortable-header font-semibold"
                onClick={() => handleSort('member_number')}
              >
                Member No. <SortIcon column="member_number" />
              </TableHead>
              <TableHead
                className="sortable-header font-semibold"
                onClick={() => handleSort('created_at')}
              >
                Registration Date <SortIcon column="created_at" />
              </TableHead>
              <TableHead
                className="sortable-header font-semibold"
                onClick={() => handleSort('first_name_1')}
              >
                First Name <SortIcon column="first_name_1" />
              </TableHead>
              <TableHead
                className="sortable-header font-semibold"
                onClick={() => handleSort('last_name')}
              >
                Last Name <SortIcon column="last_name" />
              </TableHead>
              <TableHead
                className="sortable-header font-semibold"
                onClick={() => handleSort('email')}
              >
                Email <SortIcon column="email" />
              </TableHead>
              <TableHead className="font-semibold">Role</TableHead>
              <TableHead className="font-semibold">Roles</TableHead>
              <TableHead className="font-semibold">Category</TableHead>
              <TableHead className="font-semibold">Linked Partner</TableHead>
              <TableHead className="font-semibold">Nationality</TableHead>
              <TableHead
                className="sortable-header font-semibold"
                onClick={() => handleSort('membership_status')}
              >
                Membership <SortIcon column="membership_status" />
              </TableHead>
              <TableHead className="font-semibold">Expires</TableHead>
              <TableHead className="font-semibold">{selectedYear === 'all' ? 'Paid on' : `Paid ${selectedYear}`}</TableHead>
              <TableHead className="font-semibold">Location</TableHead>
              <TableHead className="font-semibold text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedUsers.map((user, index) => (
              <TableRow 
                key={user.id} 
                className={`border-b table-row-hover ${
                  index % 2 === 0 ? 'bg-white' : 'bg-slate-25'
                }`}
              >
                <TableCell className="font-medium">
                  <span className="text-sm text-blue-700 font-mono" data-testid={`member-number-${user.id}`}>
                    {user.member_number || '—'}
                  </span>
                </TableCell>
                <TableCell className="font-medium">
                  <div className="flex items-center gap-1 text-sm">
                    <Calendar className="h-3 w-3 text-slate-500" />
                    {formatRegistrationDate(user.created_at)}
                  </div>
                </TableCell>
                <TableCell className="font-medium">{user.first_name_1} {user.first_name_2}</TableCell>
                <TableCell className="font-medium">{user.last_name}</TableCell>
                <TableCell>
                  <span className="text-sm text-slate-600">{user.email}</span>
                </TableCell>
                <TableCell>
                  <Badge variant={user.role === 'Administrator' ? 'default' : 'secondary'}>
                    {user.role || 'Regular User'}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1 items-center">
                    {user.role === 'Administrator' ? (
                      <>
                        {(user.admin_roles || []).map(role => {
                          const colors = {
                            super_admin: 'bg-red-100 text-red-800 border-red-300',
                            event_manager: 'bg-blue-100 text-blue-800 border-blue-300',
                            membership_manager: 'bg-green-100 text-green-800 border-green-300',
                            treasurer: 'bg-amber-100 text-amber-800 border-amber-300',
                          };
                          const names = { super_admin: 'Super Admin', event_manager: 'Event Mgr', membership_manager: 'Member Mgr', treasurer: 'Treasurer' };
                          return <Badge key={role} className={`text-xs ${colors[role] || ''}`}>{names[role] || role}</Badge>;
                        })}
                        {(!user.admin_roles || user.admin_roles.length === 0) && (
                          <Badge className="text-xs bg-slate-100 text-slate-600">Admin (legacy)</Badge>
                        )}
                      </>
                    ) : (
                      <span className="text-xs text-slate-400">Member</span>
                    )}
                    {isSuperAdmin && user.id !== currentUser?.id && onUpdateRoles && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-5 w-5 p-0 text-slate-400 hover:text-blue-600"
                        onClick={(e) => { e.stopPropagation(); setRolesEditUser(user); setSelectedRoles(user.admin_roles || []); }}
                        data-testid={`edit-roles-${user.id}`}
                      >
                        <Edit className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <Badge variant="outline">{user.member_category}</Badge>
                </TableCell>
                <TableCell>
                  {user.partner_info ? (
                    <div className="text-xs">
                      <div className="font-medium text-purple-700">{user.partner_info.first_name} {user.partner_info.last_name}</div>
                      <div className="text-slate-500">{user.partner_info.email}</div>
                    </div>
                  ) : (
                    <span className="text-xs text-slate-300">—</span>
                  )}
                </TableCell>
                <TableCell>
                  <span className="text-sm text-slate-600">{user.nationality || 'N/A'}</span>
                </TableCell>
                <TableCell>
                  <Badge 
                    variant={
                      user.membership_status === 'active' ? 'default' :
                      user.membership_status === 'pending_renewal' ? 'secondary' : 
                      'destructive'
                    }
                    className={`${
                      user.membership_status === 'active' ? 'bg-green-100 text-green-800' :
                      user.membership_status === 'pending_renewal' ? 'bg-yellow-100 text-yellow-800' : 
                      'bg-red-100 text-red-800'
                    }`}
                  >
                    <Shield className="h-3 w-3 mr-1" />
                    {user.membership_status === 'active' ? 'Active' :
                     user.membership_status === 'pending_renewal' ? 'Renewal' : 
                     user.membership_status || 'Active'}
                  </Badge>
                </TableCell>
                <TableCell>
                  <span className="text-xs text-slate-600">
                    {user.membership_expires ? 
                      (() => {
                        const d = new Date(user.membership_expires);
                        return `${String(d.getUTCDate()).padStart(2,'0')}/${String(d.getUTCMonth()+1).padStart(2,'0')}/${d.getUTCFullYear()}`;
                      })() : 
                      '31/12/2025'
                    }
                  </span>
                </TableCell>
                <TableCell>
                  <span className="text-xs text-slate-600" data-testid={`paid-date-${user.id}`}>{paidDateFor(user) || '—'}</span>
                </TableCell>
                <TableCell>
                  <span className="text-sm text-slate-600">
                    {user.city && user.province ? `${user.city}, ${user.province}` : 'N/A'}
                  </span>
                </TableCell>
                <TableCell>
                  <div className="flex gap-1 justify-end">
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      onClick={() => handleEditUser(user)}
                      title="Edit user"
                      className="hover:bg-blue-50 hover:text-blue-700"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={async () => {
                        const current = user.membership_year || new Date().getFullYear();
                        const raw = window.prompt(
                          `Correct membership year for ${user.first_name_1} ${user.last_name}.\nCurrent: ${current}\n\nEnter the new year (e.g. 2027):`,
                          String(current),
                        );
                        if (!raw) return;
                        const year = parseInt(raw, 10);
                        if (!year || year < 2020 || year > 2100) {
                          toast.error("Invalid year.");
                          return;
                        }
                        try {
                          await axios.patch(`/users/${user.id}/membership-year`, { year });
                          toast.success(`Membership year set to ${year}.`);
                          // Trigger a refetch by reloading — cheapest option in
                          // this admin-only affordance.
                          window.location.reload();
                        } catch (err) {
                          toast.error(err.response?.data?.detail || "Failed to update year");
                        }
                      }}
                      title="Correct membership year"
                      className="hover:bg-amber-50 hover:text-amber-700"
                      data-testid={`fix-year-btn-${user.id}`}
                    >
                      <Calendar className="h-4 w-4" />
                    </Button>
                    {onViewPaymentHistory && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => onViewPaymentHistory(user)}
                        title="Payment history"
                        className="hover:bg-emerald-50 hover:text-emerald-700"
                        data-testid={`payment-history-btn-table-${user.id}`}
                      >
                        <Receipt className="h-4 w-4" />
                      </Button>
                    )}
                    <Button 
                      size="sm" 
                      variant="ghost"
                      onClick={() => handleDeleteConfirm(user)}
                      title="Delete user"
                      className="hover:bg-red-50 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                    {isSuperAdmin && user.id !== currentUser?.id && onUpdateRoles && (
                      <Button 
                        size="sm" 
                        variant="ghost"
                        onClick={() => { setRolesEditUser(user); setSelectedRoles(user.admin_roles || []); }}
                        title="Manage admin roles"
                        className="hover:bg-green-50 hover:text-green-700"
                      >
                        <Shield className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {sortedUsers.length === 0 && (
              <TableRow>
                <TableCell colSpan={14} className="text-center text-slate-500 py-8">
                  No users found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Confirmation Dialogs */}
      <AlertDialog open={confirmDialog.isOpen} onOpenChange={(open) => !open && setConfirmDialog({ isOpen: false, type: '', user: null, onConfirm: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmDialog.type === 'delete' ? 'Delete User' : 'Promote to Administrator'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDialog.type === 'delete' 
                ? `Are you sure you want to delete "${confirmDialog.user?.first_name_1} ${confirmDialog.user?.first_name_2 ? confirmDialog.user?.first_name_2 + ' ' : ''}${confirmDialog.user?.last_name}"? This action cannot be undone and will remove all their data from the system.`
                : `Are you sure you want to promote "${confirmDialog.user?.first_name_1} ${confirmDialog.user?.first_name_2 ? confirmDialog.user?.first_name_2 + ' ' : ''}${confirmDialog.user?.last_name}" to Administrator? They will have full access to all system features.`
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDialog.onConfirm}
              className={confirmDialog.type === 'delete' ? "bg-red-600 hover:bg-red-700" : "bg-blue-600 hover:bg-blue-700"}
            >
              {confirmDialog.type === 'delete' ? 'Delete User' : 'Promote to Admin'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit User Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="h-5 w-5" />
              Edit User: {editingUser?.first_name_1} {editingUser?.first_name_2 ? editingUser?.first_name_2 + ' ' : ''}{editingUser?.last_name}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUserSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="first_name_1">First Name 1 *</Label>
                <Input
                  id="first_name_1"
                  value={userForm.first_name_1}
                  onChange={(e) => setUserForm({...userForm, first_name_1: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label htmlFor="first_name_2">First Name 2</Label>
                <Input
                  id="first_name_2"
                  value={userForm.first_name_2}
                  onChange={(e) => setUserForm({...userForm, first_name_2: e.target.value})}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="mobile">Mobile *</Label>
                <Input
                  id="mobile"
                  type="tel"
                  value={userForm.mobile}
                  onChange={(e) => setUserForm({...userForm, mobile: e.target.value})}
                  placeholder="+39 xxx xxx xxxx"
                  required
                />
              </div>
              <div>
                <Label htmlFor="landline">Landline</Label>
                <Input
                  id="landline"
                  type="tel"
                  value={userForm.landline}
                  onChange={(e) => setUserForm({...userForm, landline: e.target.value})}
                  placeholder="E.g. 0332 XXXX"
                />
              </div>
              <div>
                <Label htmlFor="last_name">Last Name *</Label>
                <Input
                  id="last_name"
                  value={userForm.last_name}
                  onChange={(e) => setUserForm({...userForm, last_name: e.target.value})}
                  required
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={userForm.email}
                onChange={(e) => setUserForm({...userForm, email: e.target.value})}
                required
              />
            </div>

            <div>
              <Label htmlFor="member_category">Member Category *</Label>
              <Select value={userForm.member_category} onValueChange={(value) => setUserForm({...userForm, member_category: value})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="JRC Internal">JRC Internal</SelectItem>
                  <SelectItem value="JRC External">JRC External</SelectItem>
                  <SelectItem value="Associated Member">Associated Member</SelectItem>
                  <SelectItem value="Standard">Standard</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="birth_date">Birth Date</Label>
                <Input
                  id="birth_date"
                  value={userForm.birth_date}
                  onChange={(e) => setUserForm({...userForm, birth_date: e.target.value})}
                  placeholder="DD/MM/YYYY"
                />
              </div>
              <div>
                <Label htmlFor="birth_place">Birth Place</Label>
                <Input
                  id="birth_place"
                  value={userForm.birth_place}
                  onChange={(e) => setUserForm({...userForm, birth_place: e.target.value})}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="nationality">Nationality</Label>
                <Input
                  id="nationality"
                  value={userForm.nationality}
                  onChange={(e) => setUserForm({...userForm, nationality: e.target.value})}
                />
              </div>
              <div>
                <Label htmlFor="other_nationality">Other Nationality</Label>
                <Input
                  id="other_nationality"
                  value={userForm.other_nationality}
                  onChange={(e) => setUserForm({...userForm, other_nationality: e.target.value})}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="address">Address</Label>
              <Input
                id="address"
                value={userForm.address}
                onChange={(e) => setUserForm({...userForm, address: e.target.value})}
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="postal_code">Postal Code</Label>
                <Input
                  id="postal_code"
                  value={userForm.postal_code}
                  onChange={(e) => setUserForm({...userForm, postal_code: e.target.value})}
                />
              </div>
              <div>
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  value={userForm.city}
                  onChange={(e) => setUserForm({...userForm, city: e.target.value})}
                />
              </div>
              <div>
                <Label htmlFor="province">Province</Label>
                <Input
                  id="province"
                  value={userForm.province}
                  onChange={(e) => setUserForm({...userForm, province: e.target.value})}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="fiscal_code">Fiscal Code</Label>
              <Input
                id="fiscal_code"
                value={userForm.fiscal_code}
                onChange={(e) => setUserForm({...userForm, fiscal_code: e.target.value})}
              />
            </div>

            <div className="flex justify-between pt-4">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setIsEditDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button type="submit">
                Update User
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Role Management Dialog */}
      <Dialog open={!!rolesEditUser} onOpenChange={(open) => { if (!open) setRolesEditUser(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Manage Admin Roles</DialogTitle>
          </DialogHeader>
          {rolesEditUser && (
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                Assign roles for <strong>{rolesEditUser.first_name_1} {rolesEditUser.last_name}</strong>
              </p>
              <div className="space-y-3">
                {ADMIN_ROLES.map(role => (
                  <label key={role.id} className="flex items-start gap-3 p-3 rounded-lg border hover:bg-slate-50 cursor-pointer" data-testid={`role-checkbox-${role.id}`}>
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={selectedRoles.includes(role.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedRoles(prev => [...prev, role.id]);
                        } else {
                          setSelectedRoles(prev => prev.filter(r => r !== role.id));
                        }
                      }}
                    />
                    <div>
                      <div className="font-medium text-sm">{role.name}</div>
                      <div className="text-xs text-slate-500">{role.desc}</div>
                    </div>
                  </label>
                ))}
              </div>
              <p className="text-xs text-slate-500">
                {selectedRoles.length === 0 
                  ? 'Removing all roles will demote this user to Regular Member.' 
                  : `Selected: ${selectedRoles.length} role(s)`}
              </p>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setRolesEditUser(null)}>Cancel</Button>
                <Button 
                  onClick={() => {
                    onUpdateRoles(rolesEditUser.id, selectedRoles);
                    setRolesEditUser(null);
                  }}
                  data-testid="save-roles-btn"
                >
                  Save Roles
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Loans Tab Content
export const LoansTabContent = ({ 
  loans, 
  users, 
  handleReturnLoan, 
  handleClearLoanHistory, 
  handleExportLoanHistory, 
  currentUser,
  loanExportFilters,
  setLoanExportFilters,
  showLoanExportDialog,
  setShowLoanExportDialog,
  onOpenAdminLoanManagement
}) => {
  const getUserName = (userId) => {
    const user = users.find(u => u.id === userId);
    return user ? `${user.first_name_1} ${user.first_name_2 ? user.first_name_2 + ' ' : ''}${user.last_name}` : "Unknown User";
  };

  const formatDate = (dateStr) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-GB');
    } catch {
      return dateStr;
    }
  };

  const getStatusBadge = (loan) => {
    const now = new Date();
    const dueDate = new Date(loan.due_date);
    
    if (loan.status === 'returned') {
      return <Badge variant="default" className="bg-green-100 text-green-800"><CheckCircle className="h-3 w-3 mr-1" />Returned</Badge>;
    } else if (loan.status === 'overdue' || (loan.status === 'active' && now > dueDate)) {
      return <Badge variant="destructive"><XCircle className="h-3 w-3 mr-1" />Overdue</Badge>;
    } else {
      return <Badge variant="secondary"><Clock className="h-3 w-3 mr-1" />Active</Badge>;
    }
  };

  const activeLoans = loans.filter(loan => loan.status !== 'returned');
  const returnedLoans = loans.filter(loan => loan.status === 'returned');

  return (
    <div className="space-y-6">
      {/* Export Controls */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="flex items-center gap-2">
              <Download className="h-5 w-5" />
              Export Loan History
            </CardTitle>
            <div className="flex gap-2">
              <Button
                onClick={() => setShowLoanExportDialog(true)}
                variant="outline"
                className="flex items-center gap-2 text-blue-700 border-blue-300 hover:bg-blue-50"
              >
                <Download className="h-4 w-4" />
                Export with Filters
              </Button>
              {currentUser?.role === 'Administrator' && onOpenAdminLoanManagement && (
                <Button
                  onClick={onOpenAdminLoanManagement}
                  variant="outline"
                  className="flex items-center gap-2 text-purple-700 border-purple-300 hover:bg-purple-50"
                >
                  <Settings className="h-4 w-4" />
                  Admin Loan Management
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex gap-2">
              <Button
                onClick={() => handleExportLoanHistory()}
                variant="outline"
                className="flex items-center gap-2"
              >
                <Download className="h-4 w-4" />
                Export All
              </Button>
              
              {currentUser?.role === 'Administrator' ? (
                <Button
                  onClick={() => handleExportLoanHistory({ status: 'active' })}
                  variant="outline"
                  className="flex items-center gap-2"
                >
                  <Clock className="h-4 w-4" />
                  Active Only
                </Button>
              ) : (
                <Button
                  onClick={() => handleExportLoanHistory({ userId: currentUser.id })}
                  variant="outline" 
                  className="flex items-center gap-2"
                >
                  <User className="h-4 w-4" />
                  My Loans
                </Button>
              )}
              
              <Button
                onClick={() => handleExportLoanHistory({ status: 'overdue' })}
                variant="outline"
                className="flex items-center gap-2 text-red-700 border-red-300 hover:bg-red-50"
              >
                <XCircle className="h-4 w-4" />
                Overdue Only
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Active Loans */}
      <div>
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Calendar className="h-5 w-5" />
          Active Loans ({activeLoans.length})
        </h3>
        <div className="rounded-lg border border-slate-200 bg-white">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Borrower</TableHead>
                <TableHead>Item</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Loan Date</TableHead>
                <TableHead>Due Date</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {activeLoans.map((loan) => (
                <TableRow key={loan.id}>
                  <TableCell className="font-medium">{getUserName(loan.user_id)}</TableCell>
                  <TableCell>{loan.media_title}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="capitalize">{loan.media_type}</Badge>
                  </TableCell>
                  <TableCell>{formatDate(loan.loan_date)}</TableCell>
                  <TableCell>{formatDate(loan.due_date)}</TableCell>
                  <TableCell>{getStatusBadge(loan)}</TableCell>
                  <TableCell>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => handleReturnLoan(loan.id)}
                      className="text-green-600 border-green-300 hover:bg-green-50"
                    >
                      <CheckCircle className="h-4 w-4 mr-1" />
                      Return
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {activeLoans.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-slate-500 py-8">
                    No active loans
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Loan History */}
      <div>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Loan History ({returnedLoans.length})</h3>
          {returnedLoans.length > 0 && handleClearLoanHistory && (
            <Button 
              variant="outline" 
              size="sm"
              onClick={handleClearLoanHistory}
              className="text-red-600 border-red-300 hover:bg-red-50"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Clear History
            </Button>
          )}
        </div>
        <div className="rounded-lg border border-slate-200 bg-white">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Borrower</TableHead>
                <TableHead>Item</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Loan Date</TableHead>
                <TableHead>Due Date</TableHead>
                <TableHead>Return Date</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {returnedLoans.slice(0, 10).map((loan) => (
                <TableRow key={loan.id}>
                  <TableCell className="font-medium">{getUserName(loan.user_id)}</TableCell>
                  <TableCell>{loan.media_title}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="capitalize">{loan.media_type}</Badge>
                  </TableCell>
                  <TableCell>{formatDate(loan.loan_date)}</TableCell>
                  <TableCell>{formatDate(loan.due_date)}</TableCell>
                  <TableCell>{loan.return_date ? formatDate(loan.return_date) : '-'}</TableCell>
                  <TableCell>{getStatusBadge(loan)}</TableCell>
                </TableRow>
              ))}
              {returnedLoans.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-slate-500 py-8">
                    No loan history
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Export Filter Dialog */}
      <Dialog open={showLoanExportDialog} onOpenChange={setShowLoanExportDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Download className="h-5 w-5" />
              Export Loan History
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Start Date</label>
                <input
                  type="date"
                  value={loanExportFilters.startDate}
                  onChange={(e) => setLoanExportFilters({...loanExportFilters, startDate: e.target.value})}
                  className="w-full p-2 border rounded-md"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">End Date</label>
                <input
                  type="date"
                  value={loanExportFilters.endDate}
                  onChange={(e) => setLoanExportFilters({...loanExportFilters, endDate: e.target.value})}
                  className="w-full p-2 border rounded-md"
                />
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium mb-1 block">Status</label>
              <select
                value={loanExportFilters.status}
                onChange={(e) => setLoanExportFilters({...loanExportFilters, status: e.target.value})}
                className="w-full p-2 border rounded-md"
              >
                <option value="">All Statuses</option>
                <option value="active">Active</option>
                <option value="returned">Returned</option>
                <option value="overdue">Overdue</option>
              </select>
            </div>

            {currentUser?.role === 'Administrator' && (
              <div>
                <label className="text-sm font-medium mb-1 block">User ID (Optional)</label>
                <input
                  type="text"
                  value={loanExportFilters.userId}
                  onChange={(e) => setLoanExportFilters({...loanExportFilters, userId: e.target.value})}
                  placeholder="Enter user ID to filter by specific user"
                  className="w-full p-2 border rounded-md"
                />
              </div>
            )}
            
            <div className="flex justify-between gap-2 pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setLoanExportFilters({ startDate: '', endDate: '', status: '', userId: '' });
                }}
              >
                Clear Filters
              </Button>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setShowLoanExportDialog(false)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    handleExportLoanHistory(loanExportFilters);
                    setShowLoanExportDialog(false);
                  }}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};